import { useEffect, useMemo, useState } from 'react';
import { seepPartnerOf, seepTeamOf, seepValue, sortHand, type SeepAction, type SeepLegal, type SeepTeamRound, type SeepView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { SeatView, MySeat, useNarrow, useSpeech } from './common';

export function SeepTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as SeepView;
  const legal = useGame((s) => s.legal) as SeepLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const [picked, setPicked] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ cards: string[]; house: number | null }>({ cards: [], house: null });
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const myTeam = mySeat !== null ? seepTeamOf(mySeat) : 0;
  const moves = new Map((legal?.moves ?? []).map((m) => [m.card, m]));
  const move = picked ? moves.get(picked) : undefined;
  const last = view.lastMove;
  const roundEnd = view.phase === 'ROUND_END' || view.phase === 'MATCH_END';
  const lastRound = view.roundResults.at(-1);
  const houseCount = (card: string) => view.houses.filter((h) => h.value === seepValue(card)).length;

  useEffect(() => {
    setPicked(null);
    setPreview({ cards: [], house: null });
  }, [view.turn, view.round, view.phase]);

  const pick = (c: string) => {
    if (!myTurn || acting) return;
    if (picked === c) {
      setPicked(null);
      setPreview({ cards: [], house: null });
      return;
    }
    setPicked(c);
    setPreview({ cards: [], house: null });
  };

  const send = (a: Omit<SeepAction, 'type' | 'card'>) => {
    if (!picked) return;
    void act({ type: 'play', card: picked, ...a });
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0} · {t('bh.cards', { n: view.capturedCounts[team] ?? 0 })}{(view.seeps[team] ?? 0) > 0 ? ` · ${view.seeps[team]}★` : ''}
    </span>
  );

  const row = (label: string, a: string | number, b: string | number, win?: [boolean, boolean]) => (
    <tr key={label}>
      <td>{label}</td>
      {[a, b].map((v, i) => (
        <td key={i} className="num" style={{ color: win?.[i] ? 'var(--accent)' : undefined }}>
          {v}
        </td>
      ))}
    </tr>
  );

  const breakdown = (res: SeepTeamRound[]) => {
    const [x, y] = [res[myTeam]!, res[1 - myTeam]!];
    return (
      <table className="scoreboard" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th />
            <th>{t('sp.us')}</th>
            <th>{t('sp.them')}</th>
          </tr>
        </thead>
        <tbody>
          {row(t('se.cards'), `${x.cards}`, `${y.cards}`)}
          {row(t('se.cardPoints'), `${x.cardPoints}`, `${y.cardPoints}`, [x.cardPoints > y.cardPoints, y.cardPoints > x.cardPoints])}
          {row(t('se.seeps'), `${x.seeps}`, `${y.seeps}`, [x.seeps > 0, y.seeps > 0])}
          {row(t('se.total'), `+${x.total}`, `+${y.total}`)}
        </tbody>
      </table>
    );
  };

  const lastText = () => {
    if (!last) return '';
    const who = name(last.seat);
    const card = shortCard(last.card);
    if (last.mode === 'trail') return `${who}: ${t('se.trailed', { card })}`;
    if (last.mode === 'build') return `${who}: ${t('se.built', { card, v: last.houseValue ?? 0 })}`;
    return `${who}: ${t('se.took', { card, n: last.taken.length + last.housesTaken })}${last.seep ? ` — ${t('se.seep')}` : ''}`;
  };

  const size = narrow ? 'sm' : 'md';

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[seepTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === seepPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {view.houses.some((h) => h.owner === s) && <span className="tag">🏠</span>}
                {!roundEnd && <span className="tag">{view.handCounts[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered" style={{ gap: 8 }}>
          <div className="center-note">
            {t('sp.round', { n: view.round })} · {t('se.deck', { n: view.deckCount })}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6, flexWrap: 'wrap' }} aria-label={t('table.scores')}>
            {teamPill(myTeam)}
            {teamPill(1 - myTeam)}
          </div>
          {roundEnd && lastRound ? (
            <div className="card glass" style={{ width: '90%', maxHeight: '44vh', overflow: 'auto' }} role="status">
              <strong>{t('se.dealResult', { n: view.roundResults.length })}</strong>
              {breakdown(lastRound)}
            </div>
          ) : (
            <div className="col" style={{ alignItems: 'center', gap: 6, maxWidth: '92%', marginTop: 8 }} aria-label={t('se.floor')}>
              {view.houses.length > 0 && (
                <div className="row" style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                  {view.houses.map((h) => (
                    <div
                      key={h.id}
                      className="row"
                      style={{ gap: 2, padding: 4, borderRadius: 12, border: `2px solid ${preview.house === h.id ? 'var(--accent)' : 'rgba(255,255,255,0.45)'}`, alignItems: 'center' }}
                      aria-label={t('se.house', { v: h.value })}
                    >
                      <span className="pill accent">🏠 {h.value}</span>
                      {h.cards.map((c) => (
                        <PlayingCard key={c} id={c} size="sm" />
                      ))}
                      <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{name(h.owner)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="row" style={{ flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                {view.loose.length === 0 && view.houses.length === 0 && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('se.empty')}</span>}
                {view.loose.map((c) => (
                  <PlayingCard key={c} id={c} size={size} selected={preview.cards.includes(c)} />
                ))}
              </div>
            </div>
          )}
          {last && !roundEnd && (
            <span className={`pill ${last.seep ? 'accent' : ''}`} role="status">
              {lastText()}
            </span>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && (
          <div className="action-bar" role="group" aria-label={t('se.choose')} style={{ flexWrap: 'wrap', maxHeight: '28vh', overflow: 'auto' }}>
            {!picked && <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)' }}>{t('se.pickCard')}</span>}
            {picked && move?.trail && (
              <button className="btn" disabled={acting} onClick={() => send({ mode: 'trail', take: [], target: null, value: 0 })}>
                {t('se.trail', { card: shortCard(picked) })}
              </button>
            )}
            {picked &&
              move?.captures.map((take, i) => (
                <button
                  key={`c${i}`}
                  className="btn primary"
                  disabled={acting}
                  onMouseEnter={() => setPreview({ cards: take, house: null })}
                  onFocus={() => setPreview({ cards: take, house: null })}
                  onClick={() => send({ mode: 'capture', take, target: null, value: 0 })}
                >
                  {t('se.take')} {[...take.map(shortCard), ...(houseCount(picked) > 0 ? [`🏠${seepValue(picked)}`] : [])].join(' + ')}
                </button>
              ))}
            {picked &&
              move?.builds.map((b, i) => (
                <button
                  key={`b${i}`}
                  className="btn"
                  disabled={acting}
                  onMouseEnter={() => setPreview({ cards: b.take, house: b.target })}
                  onFocus={() => setPreview({ cards: b.take, house: b.target })}
                  onClick={() => send({ mode: 'build', take: b.take, target: b.target, value: b.value })}
                >
                  {b.target === null ? `${t('se.build', { v: b.value })}: ${b.take.map(shortCard).join(' + ')}` : t('se.raise', { v: b.value })}
                </button>
              ))}
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.7}>
          {hand.map((c, i) => (
            <PlayingCard key={c} id={c} size={narrow ? 'md' : 'lg'} dealIndex={view.deckCount >= 32 && view.loose.length === 4 && !view.lastMove ? i : undefined} selected={picked === c} playable={Boolean(myTurn)} onClick={() => pick(c)} />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{`${name(team)} & ${name(team + 2)}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((r, i) => (
              <tr key={i}>
                <td>D{i + 1}</td>
                {r.map((x, team) => (
                  <td key={team} className="num">+{x.total}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('se.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
